import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import MaterialTable from "material-table";
import { Box, Button, Icon, Typography } from "@material-ui/core";
import { PAGE_SIZE, PAGE_SIZE_OPTIONS } from "../../../../utilities/constants";
import { useStyles } from "./styles";
import {
  getPrescriptionByCaseId,
  getPrescriptionById,
  downloadPrescription,
} from "../../PrescriptionApiActions";
import { getDxaiReportUrlRequest } from "../../../Athletes/PatientApiActions";
import DownloadIcon from "@material-ui/icons/GetApp";
import { formatDateToDDMMYYYY } from "../../../../utilities/utilityFunctions";

const PrescriptionList = ({
  caseId,
  getAllPrescriptions,
  prescriptions,
  onOpenPrescriptionForm,
  getPrescription,
  prescription,
  onSelectPrescription,
  downloadPrescription,
  getReportUrl,
  patientId,
  PatientInfo,
}) => {
  const classes = useStyles();
  const [selectedPrescriptionRow, setSelectedPrescriptionRow] = useState();
  const [paginationParams, setPaginationParams] = useState({
    page: 0,
    rowsPerPage: PAGE_SIZE,
    searchData: {
      name: "",
      active: "",
      desc: "",
    },
  });

  useEffect(() => {
    getAllPrescriptions(caseId);
  }, [getAllPrescriptions, caseId]);

  useEffect(() => {
    if (selectedPrescriptionRow) {
      getPrescription(selectedPrescriptionRow.id);
    }
  }, [getPrescription, selectedPrescriptionRow]);

  useEffect(() => {
    if (prescription && selectedPrescriptionRow) {
      onSelectPrescription(prescription);
    }
  }, [prescription, selectedPrescriptionRow]);

  // Prescriptions generated as Ayurvedic reports carry a report_id; their PDF
  // lives behind a signed url that expires, so re-sign it on every click the
  // same way the other reports do. Rows without one predate the report service
  // and still use the original renderer.
  const handleDownload = (rowData) => {
    if (rowData.reportId) {
      getReportUrl(rowData.reportId).then((url) => {
        if (url) window.open(url, "_blank", "noopener");
      });
      return;
    }

    const patientName = `${PatientInfo?.firstname || ""} ${
      PatientInfo?.lastname || ""
    }`.trim();

    downloadPrescription(patientId, rowData.id, patientName);
  };

  const columns = [
    {
      field: "Medicines",
      title: "Medicine",
      filtering: false,
      cellStyle: {
        width: "25%",
      },
      headerStyle: {
        width: "25%",
      },
      render: (rowData) => {
        if (!rowData.Medicines || rowData.Medicines.length === 0) return "-";

        return rowData.Medicines.map((med) => {
          let times = [];
          if (med.dosage.morning) times.push("morning");
          if (med.dosage.afternoon) times.push("afternoon");
          if (med.dosage.evening) times.push("evening");
          if (med.dosage.night) times.push("night");

          return `${med.medicineName}`;
        }).join(", ");
      },
    },
    {
      field: "nextFollowup",
      title: "Next Follow Up",
      filtering: false,
      render: (rowData) => {
        return formatDateToDDMMYYYY(rowData.nextFollowup) || "-";
      },
      cellStyle: {
        width: "auto",
      },
      headerStyle: {
        width: "auto",
      },
    },
    {
      field: "createdAt",
      title: "Created At",
      filtering: false,
      render: (rowData) => {
        return formatDateToDDMMYYYY(rowData.createdAt) || "-";
      },
      cellStyle: {
        width: "auto",
      },
      headerStyle: {
        width: "auto",
      },
    },
    {
      title: "Download",
      filtering: false,
      sorting: false,
      cellStyle: { width: "auto", textAlign: "center" },
      headerStyle: { textAlign: "center" },
      render: (rowData) => (
        <Button
          variant="contained"
          color="secondary"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={(event) => {
            event.stopPropagation();
            handleDownload(rowData);
          }}
        >
          PDF
        </Button>
      ),
    },
  ];

  const CustomTitle = () => (
    <Typography variant="h6" style={{ fontWeight: "bold", color: "#3f51b5" }}>
      Prescriptions
      <Button
        variant="contained"
        color="primary"
        startIcon={<Icon>add</Icon>}
        disableElevation={true}
        onClick={() => onOpenPrescriptionForm()}
        style={{ float: "right" }}
      >
        Prescription
      </Button>
    </Typography>
  );

  return (
    <Box className={classes.outerContainer}>
      <Box mt={3}>
        <Box
          sx={{
            "& .MuiToolbar-root": {
              display: "block",
              paddingTop: "12px",
            },
          }}
        >
          <MaterialTable
            title={<CustomTitle />}
            columns={columns}
            data={prescriptions.prescriptions}
            className={classes.tableWidth}
            options={{
              search: false,
              filtering: true,
              sorting: false,
              horizontalScroll: true,
              draggable: false,
              pageSizeOptions: PAGE_SIZE_OPTIONS,
              pageSize: paginationParams.rowsPerPage,
              emptyRowsWhenPaging: false,
            }}
            pages={Math.ceil(
              prescriptions.count / paginationParams.rowsPerPage
            )}
            totalCount={prescriptions.count}
            page={paginationParams.page}
            onChangeRowsPerPage={(pageSize) => {
              let data = { ...paginationParams, rowsPerPage: pageSize };
              setPaginationParams(data);
            }}
            onRowClick={(event, rowData) => {
              setSelectedPrescriptionRow(rowData);
              getPrescription(rowData.id).then((prescription) => {
                onSelectPrescription(prescription);
              });
            }}
            onChangePage={(page) => {
              let data = { ...paginationParams, page };
              setPaginationParams(data);
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

const mapStateToProps = (state) => {
  return {
    prescriptions: state.prescriptions.prescriptions,
    prescription: state.prescriptions.prescription,
    downloadedPrescriptionUrl: state.prescriptions.downloadedPrescriptionUrl,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getAllPrescriptions: (caseId, isHideLoader = false) => {
      return dispatch(getPrescriptionByCaseId(caseId, isHideLoader)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },

    getPrescription: (id, isHideLoader = false) => {
      return dispatch(getPrescriptionById(id, isHideLoader)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    downloadPrescription: (
      userId,
      prescriptionId,
      patientName,
      isHideLoader = false
    ) => {
      return dispatch(
        downloadPrescription(userId, prescriptionId, patientName, isHideLoader)
      ).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    getReportUrl: (reportId) => {
      return dispatch(getDxaiReportUrlRequest(reportId)).catch(() => null);
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(PrescriptionList);
